// UTILS
import { ref } from "vue"
import { defineStore } from "pinia"
import { getDeviceType } from "@/helpers/getDeviceType"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { sleep } from "@/lib/utils"

// TYPES
import type { Answers, Answer } from "@/features/movebis/store/movebis.types"

// STORES
import { useUserStore } from "@/stores/user"
import { useConfigStore } from "@/stores/config"
import { initAnswers, initExtra } from "@/features/movebis/store/movebis.data"
import { useQuestionsStore } from "@/features/movebis/store/questions.store"

// API
import {
  getAssessment,
  patchAssessment,
  postAssessment,
  deleteAssessment,
} from "@/api/viktor/assessment/assessment"

export const useAnswersStore = defineStore("moveBisAnswersStore", () => {
  const version = ref<string>("fr_FR_3.0.0")

  const answers = ref<Answers>(initAnswers)
  const isPatchExtraLoading = ref(false)

  const { goNext, setCurrentIndex, setDimensions, getItems } =
    useQuestionsStore()
  const userStore = useUserStore()
  const configStore = useConfigStore()

  // GETTERS

  /**
   * Sync the answers with the backend
   */
  async function syncAssessment() {
    const assessment = await getAssessment("movebis")
    const currentAnswers = answers.value

    const newLength = assessment?.data?.items?.length ?? 0
    const currentLength = answers.value?.data?.items?.length ?? 0

    answers.value = assessment

    // We don't want to override the answers if the new length is less than the current length
    if (newLength < currentLength) {
      answers.value.data.items = currentAnswers.data.items
    }

    return assessment
  }

  /**
   * Fetch the movebis assessment from the API
   * @returns The movebis assessment data
   */
  async function fetchMoveBis() {
    const assessment = await syncAssessment()
    setDimensions(assessment.dimensions)

    // if no movebis assessment, redirect to stepper
    if (assessment?.is_active !== 1) {
      document.location.href = configStore.urls.app
    } else {
      // autrement on récupère les données du test et on popule les stores
      answers.value = assessment
      if (Object.keys(answers.value?.data || {}).length === 0) {
        setCurrentIndex(0)
        answers.value.data.extra = initAnswers.data.extra
      } else {
        setCurrentIndex(answers.value.data?.items?.length)
      }

      userStore.audio = answers.value.data?.extra?.general_audio ? true : false
      answers.value.data.extra = assessment.data.extra
    }

    return answers.value
  }

  /**
   * Calculate the total elapsed time from all items
   * @returns Total elapsed time in seconds
   */
  function getElapsedTime() {
    let time = 0
    answers.value.data.items.map((item) => (time += item.time_initial))
    return Math.round(time)
  }

  // ACTIONS

  /**
   * Initialize a new movebis assessment
   * @param restarting Whether the assessment is being restarted
   * @returns The created assessment data
   */
  async function initMoveBis(restarting?: boolean) {
    const payload: Answers = {
      version: version.value,
      locale_id: 1, // force to 1 = fr_FR and not dynamic locale as locale_id.value,
      status: "todo",
      data: { items: [], extra: { ...initExtra, isRestarting: restarting } },
    }
    const data = await postAssessment("movebis", payload)
    answers.value = payload
    setCurrentIndex(0)

    return data
  }

  /**
   * Submit an answer for a specific item
   * @param itemId The ID of the item being answered
   * @param answer The answer to submit
   * @param time The time taken to answer
   */
  async function submitAnswer(itemId: number, answer: string, time: number) {
    if (
      !answers.value.data ||
      (Array.isArray(answers.value.data) && answers.value.data.length === 0)
    ) {
      answers.value.data = { items: [], extra: initExtra }
    }

    const alreadyAnswered = answers.value.data.items.find(
      (a) => a.item_id && a.item_id === itemId
    )
    if (!alreadyAnswered) {
      const item: Answer = {
        item_id: itemId,
        answer: answer,
        audio: userStore.audio,
        device_type: getDeviceType(),
        screen_width: screen.width,
        time_initial: time,
      }
      answers.value.data.items.push(item)

      answers.value.data.extra.general_audio = userStore.audio

      const payload: Answers = {
        version: version.value,
        locale_id: 1, // force to 1 = fr_FR and not dynamic locale as locale_id.value,
        data: answers.value.data,
      }
      await patchAssessment("movebis", payload)
    }
    await goNext()
  }

  /**
   * Patch extra data for the assessment
   * @param type The type of extra data to patch
   * @param answer The answer to set for the extra data
   */
  async function patchExtra(type: string, answer: boolean) {
    isPatchExtraLoading.value = true
    try {
      answers.value.data.extra[type] = answer
      answers.value.data.extra.elapsed_time = getElapsedTime()

      const payload: Answers = {
        version: version.value,
        locale_id: 1, // force to 1 = fr_FR and not dynamic locale as locale_id.value,
        data: answers.value.data,
      }
      await patchAssessment("movebis", payload)
    } finally {
      await sleep(300) // Timeout to prevent flicker
      isPatchExtraLoading.value = false
    }
  }

  /**
   * Submit the final results for the assessment
   */
  async function submitFinalResults() {
    try {
      answers.value.data.extra.elapsed_time = getElapsedTime()
      answers.value.data.extra.finished = true

      // Format final items
      const items = getItems()
      const finalItems: Answer[] = []
      items.forEach((item) => {
        finalItems.push({
          item_id: item.item_id,
          answer: null,
          audio: false,
          device_type: getDeviceType(),
          screen_width: screen.width,
          time_initial: 0,
        })
      })

      answers.value.data.items.forEach((item) => {
        const index = finalItems.findIndex((el) => el.item_id === item.item_id)
        finalItems[index] = item
      })

      // On supprime toutes les clefs item_id qui nous servait à placer les bonnes réponses
      answers.value.data.items = finalItems.map((item) => {
        delete item.item_id
        return item
      })
      // ./ Format final items

      const payload: Answers = {
        version: version.value,
        locale_id: 1, // force to 1 = fr_FR and not dynamic locale as locale_id.value,
        data: answers.value.data,
      }

      // ultime patch to finish the test
      await patchAssessment("movebis", payload)

      MixpanelInstance.track("movebis_finish", {
        payload: payload,
      })

      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  /**
   * Retake the test by deleting the current assessment and initializing a new one
   */
  async function retakeTest() {
    await deleteAssessment("movebis")
    await initMoveBis()
    await userStore.fetchAssessmentSummary()
  }

  return {
    answers,
    fetchMoveBis,
    initMoveBis,
    submitAnswer,
    patchExtra,
    isPatchExtraLoading,
    submitFinalResults,
    retakeTest,
  }
})
